import { useEffect, useMemo, useRef, useState } from 'react';
import { SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { captureException } from '~/lib/metrics';
import { GetQuoteServiceTagsFromItem } from '../lib/helpers/QuoteServiceTags';

interface request {
  provider: string;
  accountId: string;
  items: SmartSendItemApiModel[];
  discountCode?: string;
  /**
   * Held back until the caller is ready to show the result. The request fires once, the first time
   * this is true, and is never repeated - callers that need a fresh price after a change (a promo
   * code, say) go through their own verify call.
   */
  enabled?: boolean;
}

export interface verifiedBreakdown {
  loading: boolean;
  breakdown: Record<string, number> | null;
}

/**
 * Fetches the pre-booking Verify breakdown for a set of items. Verify is the only source of an
 * accurate per-item price - the quote the items carry is an estimate - so both the group total and
 * the per-service rows are derived from what this returns.
 *
 * A failure is logged and reported as "no breakdown", leaving callers to fall back to the estimate.
 */
export default function useVerifiedBreakdown(req: request): verifiedBreakdown {
  const { verifyShip } = useSmartSendApi();
  const enabled = req.enabled ?? true;

  const preBookingVerification = useMemo(
    () =>
      SettingsCache.settings.Features.includes('PreBookingVerification') ||
      req.items.some((i) => GetQuoteServiceTagsFromItem(i).includes('QuoteApi')),
    [req.items]
  );

  const [loading, setLoading] = useState(preBookingVerification);
  const [breakdown, setBreakdown] = useState<Record<string, number> | null>(null);

  // Identifies what was asked for, so a genuinely different request refetches while a StrictMode
  // remount does not. The discount code is deliberately left out: Book re-verifies with the code
  // itself and writes the result to group.Discount.Breakdown, which callers already prefer, so
  // including it here would just double the calls on every promo entry.
  const requestKey = useMemo(
    () => [req.provider, req.accountId, ...req.items.map((i) => i.Id).sort()].join('|'),
    [req.provider, req.accountId, req.items]
  );

  const requested = useRef<string | null>(null);
  const latest = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      // Mirrors the old mount/unmount semantics: leaving IDLE, or switching to another provider
      // group tab, arms a fresh verify for when the caller comes back.
      requested.current = null;
      return;
    }
    if (!preBookingVerification || requested.current === requestKey) {
      return;
    }
    requested.current = requestKey;
    latest.current = requestKey;
    setLoading(true);

    const onLoad = async () => {
      try {
        const res = await verifyShip(req.provider, req.accountId, req.items, req.discountCode ?? null);
        if (latest.current !== requestKey) {
          // Superseded by a later request - dropping this keeps a slow response for a stale item
          // set from overwriting a newer one.
          return;
        }
        setBreakdown(res?.result?.Breakdown ?? null);
      } catch (error) {
        // Log error. No need to do anything else, we'll just show the fallback breakdown if this fails.
        captureException(error, 'Error verifying provider group breakdown', {});
      } finally {
        if (latest.current === requestKey) {
          setLoading(false);
        }
      }
    };
    onLoad();
    // No cleanup flag: StrictMode remounts the effect while keeping the refs and state, so
    // abandoning the first request would leave the second run blocked by the guard and nothing
    // left to clear `loading`. Ordering is handled by `latest` instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preBookingVerification, enabled, requestKey]);

  return { loading: preBookingVerification && enabled && loading, breakdown };
}
